/**
 * 静态资源处理工具函数
 * 用于在 Vite 环境下动态获取静态资源路径
 */
import { getWebDistBaseUrl } from './oss'

// 生产构建才把图片打进模块图（hashed URL）；开发态一律源文件直链，避免百级 ?import 堵请求队列。
// #if (__APP_ENV__ === 'build')
import { uiImageUrls } from './uiImageUrls'
// #endif

// #if (__APP_ENV__ === 'build' && __PLATFORM__ === 'electron')
import { manifestImageUrls } from './manifestImageUrls'
// #endif

/**
 * 获取 assets 目录下静态资源的绝对路径
 * @param relativePath - 相对于 src/assets 的路径，如 'images/logo.png'
 * @returns 资源的绝对 URL 字符串
 */
export function getAssetUrl(relativePath: string): string {
  // 使用 import.meta.url 获取当前模块的 URL 作为基准
  // 注意：../assets/ 必须与文件实际位置对应，此文件位于 src/utils/，所以 ../assets 指向 src/assets
  return new URL(`../assets/${relativePath}`, import.meta.url).href
}

function encodeAssetPath(path: string): string {
  return path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')
}

/** 开发态：按需由浏览器拉原图，不进入 Vite 模块图 */
function resolveDevImageUrl(normalizedPath: string): string {
  return new URL(/* @vite-ignore */ `../assets/images/${normalizedPath}`, import.meta.url).href
}

function resolveManifestImageUrl(normalizedPath: string): string {
  // 网页生产：manifests 不进 assets，走 CDN / dist 根路径
  if (import.meta.env.PROD && import.meta.env.VITE_PLATFORM === 'web') {
    return `${getWebDistBaseUrl()}${encodeAssetPath(normalizedPath)}`
  }

  if (import.meta.env.DEV) {
    return resolveDevImageUrl(normalizedPath)
  }

  // Electron 生产：使用构建期 eager 的 hashed URL
  // #if (__APP_ENV__ === 'build' && __PLATFORM__ === 'electron')
  const key = `../assets/images/${normalizedPath}`
  return manifestImageUrls[key] ?? ''
  // #endif

  // #if (!(__APP_ENV__ === 'build' && __PLATFORM__ === 'electron'))
  return ''
  // #endif
}

/**
 * 动态获取图片资源 URL
 * 适用于需要动态加载 src/assets/images 下图片的场景
 *
 * @param path - 图片相对于 src/assets/images 的路径，例如 'home/banner.png'
 * @returns 图片的 URL 字符串，如果未找到则返回空字符串
 */
export function getImageUrl(path: string): string {
  const normalizedPath = path.replace(/\\/g, '/')

  if (normalizedPath.startsWith('manifests/')) {
    return resolveManifestImageUrl(normalizedPath)
  }

  // 开发态：不 eager 任何 UI 图（HAR 显示 DeviceFactory 后会瞬间打出 ~100 个 ?import）
  if (import.meta.env.DEV) {
    return resolveDevImageUrl(normalizedPath)
  }

  // #if (__APP_ENV__ === 'build')
  const imgPath = `../assets/images/${normalizedPath}`
  return uiImageUrls[imgPath] ?? ''
  // #endif

  // #if (__APP_ENV__ !== 'build')
  return ''
  // #endif
}
